import { useEffect, useState, useCallback } from "react";
import api, { errMsg, fmtMoney, fmtQty, fmtDate } from "../lib/api";
import { useI18n } from "../context/I18nContext";
import { useAuth } from "../context/AuthContext";
import { PageHeader, Empty, StatusBadge } from "../components/common";import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import { ExportButtons } from "../components/ExportButtons";

const ROTATION_META = {
  vigente: { label: "Vigente", cls: "bg-emerald-100 text-emerald-800 border-emerald-300" },
  lento: { label: "Lento", cls: "bg-amber-100 text-amber-800 border-amber-300" },
  rezagado: { label: "Rezagado", cls: "bg-red-100 text-red-800 border-red-300" },
  estrategico: { label: "Estratégico", cls: "bg-indigo-100 text-indigo-800 border-indigo-300" },
  sin_movimiento: { label: "Sin mov.", cls: "bg-slate-100 text-slate-700 border-slate-300" },
};

export default function Cardex() {
  const { t } = useI18n();
  const { can } = useAuth();
  const [rows, setRows] = useState([]);
  const [balances, setBalances] = useState([]);
  const [aggBalances, setAggBalances] = useState([]);
  const [reservations, setReservations] = useState([]);
  const [items, setItems] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [fWh, setFWh] = useState("all");
  const [fType, setFType] = useState("all");
  const [fItem, setFItem] = useState("all");
  const [rItem, setRItem] = useState("all");
  const [rSrc, setRSrc] = useState("all");
  const [open, setOpen] = useState(false);
  const [adj, setAdj] = useState({ item_number: "", warehouse: "", qty: "", unit_cost: "", reason: "" });

  const load = useCallback(() => {
    const params = {};
    if (fWh !== "all") params.warehouse = fWh;
    if (fType !== "all") params.doc_type = fType;
    if (fItem !== "all") params.item_number = fItem;
    api.get("/cardex", { params }).then((r) => setRows(r.data));
    api.get("/balances").then((r) => setBalances(r.data));
    const aggParams = fWh !== "all" ? { warehouse: fWh } : {};
    api.get("/balances-aggregate", { params: aggParams }).then((r) => setAggBalances(r.data)).catch(() => {});
  }, [fWh, fType, fItem]);

  const loadReservations = useCallback(() => {
    const params = { limit: 300 };
    if (rItem !== "all") params.item_number = rItem;
    if (rSrc !== "all") params.source_type = rSrc;
    api.get("/inventory/reservation-history", { params }).then((r) => setReservations(r.data)).catch(() => {});
  }, [rItem, rSrc]);

  useEffect(() => {
    load();
    api.get("/items").then((r) => setItems(r.data));
    api.get("/warehouses").then((r) => setWarehouses(r.data));
  }, [load]);

  useEffect(() => { loadReservations(); }, [loadReservations]);

  const saveAdj = async () => {
    try {
      await api.post("/cardex/adjustment", { ...adj, qty: Number(adj.qty), unit_cost: Number(adj.unit_cost || 0) });
      toast.success(t("common.created"));
      setOpen(false);
      setAdj({ item_number: "", warehouse: "", qty: "", unit_cost: "", reason: "" });
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const FilterSelect = ({ value, onChange, options, testid, placeholder }) => (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="rounded-none h-8 text-xs w-44" data-testid={testid}><SelectValue placeholder={placeholder} /></SelectTrigger>
      <SelectContent>
        <SelectItem value="all">{t("common.all")}</SelectItem>
        {options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
      </SelectContent>
    </Select>
  );

  return (
    <div data-testid="cardex-page">
      <PageHeader title={t("cx.title")} subtitle={t("cx.subtitle")}>
        {can("admin", "buyer", "approver", "warehouse") && (
          <>
            <ExportButtons path={`/reports/cardex?${fItem !== "all" ? `item_number=${fItem}&` : ""}${fWh !== "all" ? `warehouse=${fWh}&` : ""}${fType !== "all" ? `doc_type=${fType}&` : ""}`.replace(/[?&]$/, "")}
              filename="cardex" testid="export-cardex" />
            <ExportButtons path="/reports/balances" filename="existencias" testid="export-balances" />
          </>
        )}
        {can("admin", "warehouse") && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button size="sm" className="rounded-none text-xs font-bold" data-testid="cardex-new-adjustment-button">
                <Plus size={13} className="mr-1" /> {t("cx.newAdjustment")}
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-none">
              <DialogHeader>
                <DialogTitle className="uppercase font-black">{t("cx.adjustment")}</DialogTitle>
                <DialogDescription className="text-xs">{t("cx.qtyHint")}</DialogDescription>
              </DialogHeader>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1 col-span-2">
                  <Label className="text-[10px] font-bold uppercase tracking-widest">{t("common.item")}</Label>
                  <Select value={adj.item_number} onValueChange={(v) => setAdj({ ...adj, item_number: v })}>
                    <SelectTrigger className="rounded-none h-8 text-xs" data-testid="adj-item-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{items.map((i) => <SelectItem key={i.id} value={i.item_number}>{i.item_number} — {i.description}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-[10px] font-bold uppercase tracking-widest">{t("common.warehouse")}</Label>
                  <Select value={adj.warehouse} onValueChange={(v) => setAdj({ ...adj, warehouse: v })}>
                    <SelectTrigger className="rounded-none h-8 text-xs" data-testid="adj-warehouse-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{warehouses.map((w) => <SelectItem key={w.id} value={w.code}>{w.code} — {w.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-[10px] font-bold uppercase tracking-widest">{t("common.qty")}</Label>
                  <Input type="number" value={adj.qty} onChange={(e) => setAdj({ ...adj, qty: e.target.value })}
                    className="rounded-none h-8 text-xs" data-testid="adj-qty-input" />
                  <p className="text-[9px] text-muted-foreground">{t("cx.qtyHint")}</p>
                </div>
                <div className="space-y-1">
                  <Label className="text-[10px] font-bold uppercase tracking-widest">{t("cx.unitCost")}</Label>
                  <Input type="number" value={adj.unit_cost} onChange={(e) => setAdj({ ...adj, unit_cost: e.target.value })}
                    className="rounded-none h-8 text-xs" data-testid="adj-cost-input" />
                </div>
                <div className="space-y-1">
                  <Label className="text-[10px] font-bold uppercase tracking-widest">{t("cx.reason")}</Label>
                  <Input value={adj.reason} onChange={(e) => setAdj({ ...adj, reason: e.target.value })}
                    className="rounded-none h-8 text-xs" data-testid="adj-reason-input" />
                </div>
              </div>
              <Button onClick={saveAdj} disabled={!adj.item_number || !adj.warehouse || !adj.qty}
                className="rounded-none font-bold" data-testid="adj-save-button">{t("common.save")}</Button>
            </DialogContent>
          </Dialog>
        )}
      </PageHeader>

      <Tabs defaultValue="movements">
        <TabsList className="rounded-none">
          <TabsTrigger value="movements" className="rounded-none text-xs" data-testid="tab-movements">{t("cx.movements")}</TabsTrigger>
          <TabsTrigger value="balances" className="rounded-none text-xs" data-testid="tab-balances">{t("cx.balances")}</TabsTrigger>
          <TabsTrigger value="reservations" className="rounded-none text-xs" data-testid="tab-reservations">Reservas históricas</TabsTrigger>
        </TabsList>
        <TabsContent value="movements">
          <div className="flex gap-2 my-3 flex-wrap">
            <FilterSelect value={fItem} onChange={setFItem} testid="cardex-filter-item"
              options={items.map((i) => ({ value: i.item_number, label: i.item_number }))} placeholder={t("common.item")} />
            <FilterSelect value={fWh} onChange={setFWh} testid="cardex-filter-warehouse"
              options={warehouses.map((w) => ({ value: w.code, label: w.code }))} placeholder={t("common.warehouse")} />
            <FilterSelect value={fType} onChange={setFType} testid="cardex-filter-doctype"
              options={[{ value: "IN", label: "IN" }, { value: "RC", label: "RC" }, { value: "AJ", label: "AJ" }]} placeholder={t("cx.docType")} />
          </div>
          {rows.length === 0 ? <Empty text={t("common.noData")} /> : (
            <div className="tbl-wrap" data-testid="cardex-table">
              <table className="tbl">
                <thead><tr>
                  <th>{t("cx.docType")}</th><th>{t("cx.docNumber")}</th><th>{t("common.item")}</th>
                  <th>{t("common.warehouse")}</th><th>{t("loc.bin")}</th><th>{t("common.status")}</th>
                  <th className="num">{t("common.qty")}</th><th>{t("common.uom")}</th>
                  <th className="num">{t("cx.unitCost")}</th><th className="num">{t("cx.totalCost")}</th>
                  <th>Equipo</th>
                  <th>{t("common.user")}</th><th>{t("cx.reference")}</th><th>{t("common.date")}</th>
                </tr></thead>
                <tbody>
                  {rows.map((c) => (
                    <tr key={c.id}>
                      <td className="font-black">{c.doc_type}</td>
                      <td className="font-medium">{c.doc_number}</td>
                      <td>{c.item_number} — {c.item_description}</td>
                      <td>{c.warehouse}</td>
                      <td>{c.location || "—"}</td>
                      <td><StatusBadge status={c.stock_status || "available"} /></td>
                      <td className={`num font-bold ${c.qty < 0 ? "text-destructive" : "text-green-700 dark:text-green-400"}`}>{c.qty > 0 ? "+" : ""}{fmtQty(c.qty)}</td>
                      <td>{c.uom}</td>
                      <td className="num">{fmtMoney(c.unit_cost)}</td>
                      <td className="num">{fmtMoney(c.total_cost)}</td>
                      <td className="font-mono text-[10px] font-bold">{c.equipment_code || "—"}</td>
                      <td className="text-muted-foreground">{c.user_email}</td>
                      <td className="text-muted-foreground">{c.reference}</td>
                      <td className="text-muted-foreground">{fmtDate(c.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </TabsContent>
        <TabsContent value="balances">
          <div className="flex gap-2 my-3 flex-wrap items-center">
            <FilterSelect value={fWh} onChange={setFWh} testid="bal-filter-warehouse"
              options={warehouses.map((w) => ({ value: w.code, label: `${w.code} — ${w.name}` }))}
              placeholder="Almacén" />
            <Button size="sm" variant="outline" onClick={() => {
              const params = fWh !== "all" ? `?warehouse=${fWh}` : "";
              window.open(`${process.env.REACT_APP_BACKEND_URL}/api/balances-export${params}`, "_blank");
            }} className="rounded-none text-xs font-bold h-8" data-testid="bal-export-csv">
              Descargar CSV
            </Button>
            <div className="text-[10px] text-muted-foreground ml-auto">
              Vista agregada por almacén · incluye categoría y rotación
            </div>
          </div>
          <div className="tbl-wrap mt-3" data-testid="balances-table">
            <table className="tbl">
              <thead><tr>
                <th>{t("common.item")}</th><th>{t("common.description")}</th>
                <th>Categoría</th>
                <th>{t("common.warehouse")}</th>
                <th className="num">Existencia</th>
                <th className="num">Reservado</th>
                <th className="num">Disponible</th>
                <th>{t("common.uom")}</th>
                <th className="num">{t("cx.avgCost")}</th>
                <th className="num">{t("cx.value")}</th>
                <th>Rotación</th>
              </tr></thead>
              <tbody>
                {aggBalances.map((b, i) => {
                  const rot = ROTATION_META[b.rotation_status] || ROTATION_META.sin_movimiento;
                  return (
                    <tr key={`${b.item_number}-${b.warehouse}-${i}`}>
                      <td className="font-bold">{b.item_number}</td>
                      <td>{b.description}</td>
                      <td className="text-[11px]">{b.family_name || b.category || "—"}</td>
                      <td>{b.warehouse}</td>
                      <td className="num font-bold">{fmtQty(b.qty_on_hand)}</td>
                      <td className="num text-muted-foreground">{fmtQty(b.qty_reserved)}</td>
                      <td className="num font-bold">{fmtQty(b.qty_available)}</td>
                      <td>{b.uom}</td>
                      <td className="num">{fmtMoney(b.avg_cost)}</td>
                      <td className="num font-bold">{fmtMoney(b.value)}</td>
                      <td>
                        <span className={`inline-block text-[10px] font-black uppercase px-1.5 py-0.5 border ${rot.cls}`}>
                          {rot.label}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </TabsContent>
        <TabsContent value="reservations">
          <div className="flex gap-2 my-3 flex-wrap">
            <FilterSelect value={rItem} onChange={setRItem} testid="rsv-filter-item"
              options={items.map((i) => ({ value: i.item_number, label: i.item_number }))} placeholder={t("common.item")} />
            <FilterSelect value={rSrc} onChange={setRSrc} testid="rsv-filter-source"
              options={[{ value: "AV", label: "AV — Aviso PM" }, { value: "OT", label: "OT — Trabajo" }, { value: "CLEANUP", label: "CLEANUP — Aseo" }]}
              placeholder="Origen" />
          </div>
          {reservations.length === 0 ? <Empty text="Sin movimientos de reserva" /> : (
            <div className="tbl-wrap" data-testid="reservations-table">
              <table className="tbl">
                <thead><tr>
                  <th>Fecha</th><th>Artículo</th><th>Almacén</th>
                  <th className="num">Delta</th><th className="num">Antes</th><th className="num">Después</th>
                  <th>Origen</th><th>Documento</th><th>Motivo</th><th>Usuario</th>
                </tr></thead>
                <tbody>
                  {reservations.map((h) => (
                    <tr key={h.id}>
                      <td className="text-[10px] text-muted-foreground">{fmtDate(h.at)}</td>
                      <td className="font-bold">{h.item_number}</td>
                      <td>{h.warehouse}</td>
                      <td className={`num font-bold tabular-nums ${h.delta < 0 ? "text-emerald-700" : "text-blue-700"}`}>
                        {h.delta > 0 ? "+" : ""}{fmtQty(h.delta)}
                      </td>
                      <td className="num tabular-nums text-muted-foreground">{fmtQty(h.before)}</td>
                      <td className="num tabular-nums font-bold">{fmtQty(h.after)}</td>
                      <td>
                        {h.source_type ? (
                          <span className={`inline-block text-[10px] font-black uppercase px-1.5 py-0.5 border ${
                            h.source_type === "AV" ? "bg-amber-100 text-amber-800 border-amber-300" :
                            h.source_type === "OT" ? "bg-blue-100 text-blue-800 border-blue-300" :
                            h.source_type === "CLEANUP" ? "bg-purple-100 text-purple-800 border-purple-300" :
                            "bg-slate-100 text-slate-800 border-slate-300"}`}>
                            {h.source_type}
                          </span>
                        ) : "—"}
                      </td>
                      <td className="font-medium text-[11px]">{h.source_doc || "—"}</td>
                      <td className="text-[11px] text-muted-foreground">{h.reason}</td>
                      <td className="text-[10px] text-muted-foreground">{h.by}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
